import { booleanAttribute, Directive, effect, input } from '@angular/core';
import { NgpPlacement } from 'ng-primitives/portal';
import { injectTooltipTriggerState, NgpTooltipTrigger } from 'ng-primitives/tooltip';
import { TooltipComponent, TooltipContent } from './tooltip.component';

export type TooltipPlacement = NgpPlacement;

/** Leaves room for the half of the beak that sits outside the bubble, plus a small gap. */
const TOOLTIP_OFFSET = 8;

/**
 * Shows a tooltip while the host element is hovered or focused.
 *
 * Pointer events do not fire on disabled controls, so wrap a disabled trigger in an element
 * that carries the tooltip instead of putting it on the control itself.
 */
@Directive({
  selector: '[joiiTooltip]',
  hostDirectives: [
    {
      directive: NgpTooltipTrigger,
      inputs: [
        'ngpTooltipTriggerPlacement: tooltipPlacement',
        'ngpTooltipTriggerShowDelay: tooltipShowDelay',
        'ngpTooltipTriggerHideDelay: tooltipHideDelay',
      ],
    },
  ],
})
export class TooltipDirective {
  /** Primary line of the tooltip. No tooltip is shown while this and the secondary line are empty. */
  readonly text = input<string>('', { alias: 'joiiTooltip' });
  /** Supporting line rendered underneath the primary line. */
  readonly secondaryText = input<string>('', { alias: 'tooltipSecondaryText' });
  readonly disabled = input(false, { alias: 'tooltipDisabled', transform: booleanAttribute });

  constructor() {
    const trigger = injectTooltipTriggerState<TooltipContent>();

    trigger().setTooltip(TooltipComponent);
    trigger().setOffset(TOOLTIP_OFFSET);

    effect(() => {
      const text = this.text();
      const secondaryText = this.secondaryText();

      trigger().setContext({ text, secondaryText });
      trigger().setDisabled(this.disabled() || (!text && !secondaryText));
    });
  }
}
